import { Badge, type badgeVariants } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { VariantProps } from 'class-variance-authority';
import type { ReactElement } from 'react';
import {
  CATALOG_TIERS,
  catalogRuleOutput,
  INSTANT_SIGNAL_WEIGHT,
  type CatalogRuleDisplay,
} from '../constants/builtin-catalog';

function formatWeight(rule: CatalogRuleDisplay): string {
  if (rule.weight >= INSTANT_SIGNAL_WEIGHT) return 'Instant';
  return String(rule.weight);
}

type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

// Colors the Weight badge by the rule's severity, reusing the shared severity
// Badge variants (low/medium/high/critical → slate/amber/orange/red). Purely
// informational rows (severity 'info') get the muted Low treatment.
const SEVERITY_VARIANT: Record<CatalogRuleDisplay['severity'], BadgeVariant> = {
  info: 'low',
  low: 'low',
  medium: 'medium',
  high: 'high',
  critical: 'critical',
};

// Column widths (fr units, summing to ~12 to preserve the original layout).
// Match Type / Output are reduced by 1/3 (2 → 1.33) and the freed width is
// handed to Description (2 → 4).
const GRID_TEMPLATE_COLUMNS = '3fr 1.33fr 1.33fr 1fr 4fr';

/**
 * Read-only panel displaying the built-in extension security detection catalog.
 * Rules are grouped by tier: AI-IDE, Capability Registration, Activation Patterns,
 * and Supply Chain / Trust Posture.
 *
 * Output is decided by the aggregate weighted score; the panel surfaces each
 * rule's Weight, colored by the rule's severity.
 */
export function BuiltInCatalogPanel(): ReactElement {
  return (
    <div className="space-y-4" data-testid="builtin-catalog-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Built-in Detection Catalog</h4>
        <Badge variant="secondary" className="text-[10px]">
          Read only
        </Badge>
      </div>

      <p className="text-xs text-muted-foreground">
        These rules are evaluated automatically against extension manifests when
        capability detection is enabled. Each matched rule adds its weight to an
        aggregate score; a score of 50+ produces a finding. Instant rules always
        produce a finding on their own.
      </p>

      <div className="space-y-6">
        {CATALOG_TIERS.map(tier => (
          <div key={tier.name} className="space-y-2">
            <h5 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
              {tier.label}
            </h5>

            <div className="border border-table-border rounded-md overflow-hidden text-xs">
              {/* Table header */}
              <div
                className="grid gap-1 bg-muted min-h-9 items-center border-b"
                style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
              >
                <div className="px-3 font-medium text-foreground/80">
                  Manifest Field
                </div>
                <div className="px-2 font-medium text-foreground/80">
                  Match Type
                </div>
                <div className="px-2 font-medium text-foreground/80">
                  Output
                </div>
                <div className="px-2 font-medium text-foreground/80">
                  Weight
                </div>
                <div className="px-2 font-medium text-foreground/80">
                  Description
                </div>
              </div>

              {/* Table rows */}
              {tier.rules.map(rule => (
                <div
                  key={rule.id}
                  className="grid gap-1 min-h-10 items-center border-b last:border-b-0 hover:bg-muted/30"
                  style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                >
                  <div className="px-3 py-2 truncate">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded cursor-default">
                          {rule.field}
                        </code>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>{rule.field}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="px-2 py-2">
                    <Badge variant="outline" className="text-[10px]">
                      {rule.matchType}
                    </Badge>
                  </div>
                  <div className="px-2 py-2">
                    <Badge
                      variant={
                        catalogRuleOutput(rule) === 'finding'
                          ? 'destructive'
                          : 'secondary'
                      }
                      className="text-[10px]"
                    >
                      {catalogRuleOutput(rule) === 'finding'
                        ? 'Finding'
                        : 'Capability Metadata'}
                    </Badge>
                  </div>
                  <div className="px-2 py-2">
                    <Badge
                      variant={SEVERITY_VARIANT[rule.severity]}
                      className="text-[10px]"
                    >
                      {formatWeight(rule)}
                    </Badge>
                  </div>
                  <div className="px-2 py-2 truncate text-muted-foreground">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {rule.description}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="max-w-xs">{rule.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
